import React, { useEffect, useState } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { CircleFlag } from "react-circle-flags";
import { Color } from "../../../utils/variables";
import styled from "styled-components";

export const DonutChart = ({
  data,
  id = "donutChartDiv",
  size,
  color = "green",
}) => {
  const [slicePositions, setSlicePositions] = useState([]);

  useEffect(() => {
    // Enable animations
    am4core.useTheme(am4themes_animated);

    // Create chart instance
    const chart = am4core.create(id, am4charts.PieChart);

    // Hide amCharts logo/watermark
    chart?.logo && (chart.logo.disabled = true);

    // Set default font for the entire chart
    chart.fontFamily = "Oxygen-Regular";

    chart.data = data?.map((item) => ({
      category: item.country,
      value: item.count,
      code: item.countrycode,
    }));

    const pieSeries = chart.series.push(new am4charts.PieSeries());
    pieSeries.dataFields.value = "value";
    pieSeries.dataFields.category = "country";

    // Configure slice colors
    pieSeries.slices.template.adapter.add("fill", (fill, target) => {
      if (target.dataItem && target.dataItem.dataContext.code === "xx") {
        return am4core.color(Color("gray", 4));
      }
      return fill; // Return default color for other slices
    });

    // Configure value labels
    pieSeries.labels.template.disabled = false;
    pieSeries.labels.template.text = "{value.percent.formatNumber('#.#')}%";
    pieSeries.labels.template.fill = am4core.color("#000000");
    pieSeries.labels.template.fontSize = 12;
    pieSeries.labels.template.fontFamily = "Oxygen-Regular";

    // Configure dynamic label radius
    pieSeries.labels.template.adapter.add("radius", function (radius, target) {
      if (!target.dataItem) return radius;

      // Get the slice index
      const index = target.dataItem.index;

      // Define different radii based on position
      const radii = [10, 30, 10, 30, 10, 30, 10];
      return radii[index] || 50;
    });

    pieSeries.alignLabels = false;
    pieSeries.labels.template.relativeRotation = 0;
    pieSeries.labels.template.padding(0, 0, 0, 0);

    // Ensure labels stay visible
    pieSeries.labels.template.keepTargetHover = true;
    pieSeries.labels.template.disabled = false;

    // Calculate positions after the chart is fully rendered
    pieSeries.events.on("ready", () => {
      updatePositions(pieSeries, chart);

      // Configure tooltips after flags are rendered
      pieSeries.slices.template.tooltipText =
        "{category}: {value.percent.formatNumber('#.#')}%";
      pieSeries.tooltip.disabled = false;

      // Configure tooltip container
      pieSeries.tooltip.background.fillOpacity = 0.9;
      pieSeries.tooltip.background.strokeWidth = 0;
      pieSeries.tooltip.getFillFromObject = false;
      pieSeries.tooltip.background.fill = am4core.color("#ffffff");
      pieSeries.tooltip.background.filters.clear();
      pieSeries.tooltip.background.cornerRadius = 4;
      pieSeries.tooltip.label.fill = am4core.color("#000000");
      pieSeries.tooltip.label.fontFamily = "Oxygen-Regular";
      pieSeries.tooltip.label.fontSize = 12;
      pieSeries.tooltip.label.padding(8, 12, 8, 12);

      // Move tooltip outside of chart container
      pieSeries.tooltip.pointerOrientation = "horizontal";
      pieSeries.tooltip.dx = 0;
      pieSeries.tooltip.dy = 0;
      pieSeries.tooltip.animationDuration = 150;
      pieSeries.tooltip.keepTargetHover = true;

      // Ensure tooltip is above everything
      pieSeries.tooltip.zIndex = 1001;
      chart.maxZIndex = 1000;

      // Add click handler for tooltips
      var currentSlice;
      pieSeries.slices.template.events.on("hit", function (ev) {
        if (currentSlice) {
          currentSlice.tooltip.hide();
          if (currentSlice === ev.target) {
            currentSlice = null;
            return;
          }
        }
        currentSlice = ev.target;
        currentSlice.tooltip.show();
      });

      // Hide tooltip when clicking outside
      chart.events.on("hit", function (ev) {
        if (currentSlice && !ev.target.parent.parent.parent.isSprite) {
          currentSlice.tooltip.hide();
          currentSlice = null;
        }
      });
    });

    // Update positions when chart is resized
    chart.events.on("sizechanged", () => {
      updatePositions(pieSeries, chart);
    });

    // Update positions when slices move
    pieSeries.events.on("positionchanged", () => {
      updatePositions(pieSeries, chart);
    });

    pieSeries.slices.template.cursorOverStyle =
      am4core.MouseCursorStyle.pointer;
    pieSeries.radius = am4core.percent(90);

    // Custom colors
    pieSeries.colors.list = [
      am4core.color(Color(color, 9)),
      am4core.color(Color(color, 8)),
      am4core.color(Color(color, 7)),
      am4core.color(Color(color, 6)),
      am4core.color(Color(color, 5)),
      am4core.color(Color(color, 4)),
      am4core.color(Color(color, 3)),
      am4core.color(Color(color, 2)),
      am4core.color(Color(color, 1)),
    ];

    return () => {
      chart.dispose();
    };
  }, [data]);

  const updatePositions = (pieSeries, chart) => {
    const positions = pieSeries.slices.values.map((slice, index) => {
      // Get the middle radius and adjust it for flags
      const middleAngle = slice.middleAngle;
      const angleRad = (middleAngle * Math.PI) / 180;

      // Define different radii for flags
      const flagRadii = [
        size / 5,
        size / 3.5,
        size / 5,
        size / 3.5,
        size / 5,
        size / 3.5,
        size / 5,
      ];
      const adjustedRadius = flagRadii[index] || 90;

      return {
        code: slice.dataItem.dataContext.code?.toLowerCase(),
        x: chart.pixelWidth / 2 + adjustedRadius * Math.cos(angleRad),
        y: chart.pixelHeight / 2 + adjustedRadius * Math.sin(angleRad),
      };
    });
    setSlicePositions(positions);
  };

  return (
    <div
      style={{
        position: "relative",
        width: `${size}px`,
        height: `${size}px`,
      }}
    >
      <div
        id={id}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          position: "relative",
          top: 0,
          left: 0,
        }}
      />
      {slicePositions.map((position, index) => (
        <div
          key={index}
          style={{
            position: "absolute",
            transform: `translate(-50%, -50%)`,
            left: `${position.x}px`,
            top: `${position.y}px`,
            zIndex: 1,
            pointerEvents: "none",
            border:
              position.code === "xx"
                ? "none"
                : size === 400
                ? "3px solid white"
                : "2px solid white",

            borderRadius: "50%", // Make container perfectly circular
            height: size === 400 ? "32px" : "24px",
            width: size === 400 ? "32px" : "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {position.code !== "xx" && (
            <CircleFlag
              className="flag"
              countryCode={position.code}
              height={size === 400 ? 26 : 20}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default DonutChart;
